import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ConfirmModal, Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api } from '@/lib/api';
import { roleActionErrorToast } from '@/lib/user-feedback';
import type { LegalPolicyTemplateDetail } from './types';
import { legalPolicyTypeLabel } from './legal-policy-type-labels';

const FIELD =
  'w-full rounded-lg border border-dash-outline-variant bg-white px-4 py-3 text-base text-dash-on-surface outline-none transition-all focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20';

const LABEL = 'text-sm font-medium text-dash-on-surface-variant';

function SectionHeading({ icon, title }: { icon: string; title: string }) {
  return (
    <div className="mb-6 flex items-center gap-2">
      <span className="material-symbols-outlined text-dash-primary" aria-hidden="true">
        {icon}
      </span>
      <h2 className="text-xl font-semibold text-dash-on-surface">{title}</h2>
    </div>
  );
}

function SwitchField({
  id,
  checked,
  onChange,
  label,
  hint,
}: {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  hint?: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <label htmlFor={id} className="flex cursor-pointer items-center gap-3">
        <span className="relative inline-block h-6 w-11 shrink-0">
          <input
            id={id}
            type="checkbox"
            className="peer sr-only"
            checked={checked}
            onChange={(event) => onChange(event.target.checked)}
          />
          <span
            className="block h-6 w-11 rounded-full bg-secondary-200 transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-dash-secondary/20 peer-checked:bg-dash-primary"
            aria-hidden="true"
          />
          <span
            className="pointer-events-none absolute left-0.5 top-0.5 block h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5"
            aria-hidden="true"
          />
        </span>
        <span className="text-sm font-medium text-dash-on-surface">{label}</span>
      </label>
      {hint ? <p className="text-xs text-dash-on-surface-variant">{hint}</p> : null}
    </div>
  );
}

export function LegalPolicyEditorPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [version, setVersion] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [consentLabel, setConsentLabel] = useState('');
  const [linkLabel, setLinkLabel] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [requiredAtRegister, setRequiredAtRegister] = useState(false);
  const [requiredAtContractSigning, setRequiredAtContractSigning] = useState(true);
  const [blocksAccessUntilAccepted, setBlocksAccessUntilAccepted] = useState(false);

  const { data: template, isLoading } = useQuery({
    queryKey: ['legal-policies', 'templates', id],
    queryFn: () => api<LegalPolicyTemplateDetail>(`/legal/policies/templates/${id}`),
    enabled: Boolean(id),
  });

  useEffect(() => {
    if (!template) return;
    setTitle(template.title);
    setSlug(template.slug);
    setVersion(template.version);
    setBodyHtml(template.bodyHtml);
    setConsentLabel(template.consentLabel);
    setLinkLabel(template.linkLabel);
    setIsActive(template.isActive);
    setRequiredAtRegister(template.requiredAtRegister);
    setRequiredAtContractSigning(template.requiredAtContractSigning);
    setBlocksAccessUntilAccepted(template.blocksAccessUntilAccepted);
  }, [template]);

  const saveMutation = useMutation({
    mutationFn: () =>
      api(`/legal/policies/templates/${id}`, {
        method: 'PATCH',
        body: {
          title,
          slug,
          version,
          bodyHtml,
          consentLabel,
          linkLabel,
          isActive,
          requiredAtRegister,
          requiredAtContractSigning,
          blocksAccessUntilAccepted,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['legal-policies', 'templates'] });
      void queryClient.invalidateQueries({ queryKey: ['legal-policies', 'templates', id] });
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo guardar la política legal'));
    },
  });

  if (isLoading || !template) {
    return (
      <div className="lms-site-admin-hub-container py-8 md:py-12">
        <Loader label="Cargando plantilla…" />
      </div>
    );
  }

  return (
    <div className="lms-legal-policies-admin lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex items-start gap-4">
        <HubBackLink to="/politicas-legales" label="Volver a políticas legales" />
        <div>
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Editar política legal</h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            {legalPolicyTypeLabel(template.type)} · {template.slug} · v{template.version}
          </p>
        </div>
      </div>

      <div className="lms-credentials-glass-panel overflow-hidden rounded-xl shadow-sm">
        <section className="border-b border-dash-outline-variant p-6 md:p-8">
          <SectionHeading icon="policy" title="Datos de la plantilla" />
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <label htmlFor="legal-policy-type" className={LABEL}>
                Tipo de política
              </label>
              <input
                id="legal-policy-type"
                className={`${FIELD} bg-secondary-50`}
                value={legalPolicyTypeLabel(template.type)}
                readOnly
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="legal-policy-version" className={LABEL}>
                Versión <span className="text-error-600">*</span>
              </label>
              <input
                id="legal-policy-version"
                className={FIELD}
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="legal-policy-edit-title" className={LABEL}>
                Título <span className="text-error-600">*</span>
              </label>
              <input
                id="legal-policy-edit-title"
                className={FIELD}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="legal-policy-edit-slug" className={LABEL}>
                Slug <span className="text-error-600">*</span>
              </label>
              <input
                id="legal-policy-edit-slug"
                className={FIELD}
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                required
              />
            </div>
          </div>
        </section>

        <section className="border-b border-dash-outline-variant p-6 md:p-8">
          <SectionHeading icon="edit_note" title="Textos de consentimiento" />
          <div className="grid grid-cols-1 gap-6">
            <div className="space-y-2">
              <label htmlFor="legal-policy-consent" className={LABEL}>
                Texto del checkbox
              </label>
              <textarea
                id="legal-policy-consent"
                rows={3}
                className={`${FIELD} resize-none`}
                value={consentLabel}
                onChange={(e) => setConsentLabel(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="legal-policy-link" className={LABEL}>
                Texto del enlace
              </label>
              <input
                id="legal-policy-link"
                className={FIELD}
                value={linkLabel}
                onChange={(e) => setLinkLabel(e.target.value)}
              />
            </div>
          </div>
        </section>

        <section className="border-b border-dash-outline-variant p-6 md:p-8">
          <SectionHeading icon="toggle_on" title="Cuándo se solicita" />
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <SwitchField
              id="legal-policy-active"
              checked={isActive}
              onChange={(next) => {
                if (!next && isActive) {
                  setDeactivateOpen(true);
                  return;
                }
                setIsActive(next);
              }}
              label="Plantilla activa"
            />
            <SwitchField
              id="legal-policy-register"
              checked={requiredAtRegister}
              onChange={setRequiredAtRegister}
              label="Requerida en registro"
            />
            <SwitchField
              id="legal-policy-contract"
              checked={requiredAtContractSigning}
              onChange={setRequiredAtContractSigning}
              label="Requerida al firmar contrato"
              hint="Si está activa, el checkbox aparece en la pantalla de firma del contrato (junto a la firma y la aceptación del contrato)."
            />
            <SwitchField
              id="legal-policy-blocks"
              checked={blocksAccessUntilAccepted}
              onChange={setBlocksAccessUntilAccepted}
              label="Bloquea acceso hasta aceptar"
            />
          </div>
        </section>

        <section className="border-b border-dash-outline-variant p-6 md:p-8">
          <SectionHeading icon="article" title="Contenido HTML" />
          <div className="space-y-2">
            <label htmlFor="legal-policy-body" className={LABEL}>
              Cuerpo de la política
            </label>
            <textarea
              id="legal-policy-body"
              className={`${FIELD} min-h-[28rem] resize-y font-mono text-sm`}
              value={bodyHtml}
              onChange={(e) => setBodyHtml(e.target.value)}
            />
          </div>
        </section>

        <footer className="flex flex-col justify-end gap-4 bg-secondary-50/80 p-6 sm:flex-row md:p-8">
          <Link
            to="/politicas-legales"
            className="inline-flex items-center justify-center rounded-lg border border-dash-outline-variant px-8 py-3 text-sm font-medium text-dash-primary transition-colors hover:bg-secondary-100"
          >
            Cancelar
          </Link>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-lg bg-dash-primary px-8 py-3 text-sm font-medium text-white shadow-sm transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60"
            disabled={saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </footer>
      </div>

      <ConfirmModal
        open={deactivateOpen}
        title="Desactivar plantilla"
        description="Los usuarios dejarán de ver esta política. Quienes aún no la aceptaron no tendrán que hacerlo, y dejará de exigirse en el registro, en la firma del contrato y como bloqueo de acceso."
        confirmLabel="Desactivar"
        onConfirm={() => {
          setIsActive(false);
          setDeactivateOpen(false);
        }}
        onClose={() => setDeactivateOpen(false)}
      />
    </div>
  );
}
